import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { adminAPI } from '@/lib/api';
import { toast } from 'sonner';
import { 
  Users, TrendingUp, Clock, Upload, Activity, 
  AlertCircle, Download, Search, Filter, Trash2, Edit, X,
  RefreshCw, AlertTriangle, ChevronDown
} from 'lucide-react';
import useSEO from '@/hooks/useSEO';

const SUPER_ADMIN_EMAIL = 'factfrenzyofcl@gmail.com';

const Admin = () => {
  useSEO({ title: 'Admin — EpisodeKit', path: '/admin', index: false });
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [overview, setOverview] = useState({
    totalUsers: 0,
    newUsers24h: 0,
    newUsers7d: 0,
    newUsers30d: 0,
    mau: 0,
    totalMinutes7d: 0,
    totalMinutes30d: 0,
    totalMinutesAllTime: 0,
    totalUploads7d: 0,
    totalUploads30d: 0,
    trialUsageBuckets: {},
    processingSuccessRate7d: 0,
    processingSuccessRate30d: 0,
    topErrors7d: []
  });
  const [users, setUsers] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // User list filters
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Monthly chart config
  const [monthsToShow, setMonthsToShow] = useState(12);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  
  // Modal states
  const [changePlanModal, setChangePlanModal] = useState({ open: false, user: null, selectedPlan: '' });
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null, confirmText: '' });
  
  // Cost analytics state
  const [costReport, setCostReport] = useState(null);
  const [costLoading, setCostLoading] = useState(false);
  const [showCostAnalytics, setShowCostAnalytics] = useState(false);

  // Check if current user is super admin
  const isSuperAdmin = user?.email?.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!user?.is_admin) {
      toast.error('Access denied. Admin privileges required.');
      navigate('/');
      return;
    }
    
    loadData();
  }, [isAuthenticated, user, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load overview with defensive defaults
      const overviewData = await adminAPI.getOverview();
      
      setOverview({
        totalUsers: overviewData?.totalUsers ?? 0,
        newUsers24h: overviewData?.newUsers24h ?? 0,
        newUsers7d: overviewData?.newUsers7d ?? 0,
        newUsers30d: overviewData?.newUsers30d ?? 0,
        mau: overviewData?.mau ?? 0,
        totalMinutes7d: overviewData?.totalMinutes7d ?? 0,
        totalMinutes30d: overviewData?.totalMinutes30d ?? 0,
        totalMinutesAllTime: overviewData?.totalMinutesAllTime ?? 0,
        totalUploads7d: overviewData?.totalUploads7d ?? 0,
        totalUploads30d: overviewData?.totalUploads30d ?? 0,
        trialUsageBuckets: overviewData?.trialUsageBuckets ?? {},
        processingSuccessRate7d: overviewData?.processingSuccessRate7d ?? 0,
        processingSuccessRate30d: overviewData?.processingSuccessRate30d ?? 0,
        topErrors7d: Array.isArray(overviewData?.topErrors7d) ? overviewData.topErrors7d : []
      });
      
      // Load users
      await loadUsers();
      
      // Load monthly analytics with defensive defaults
      const monthlyRes = await adminAPI.getMonthlyAnalytics(monthsToShow);
      const monthlyArray = monthlyRes?.data ?? [];
      setMonthlyData(Array.isArray(monthlyArray) ? monthlyArray : []);
      
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async (page = 1) => {
    try {
      const response = await adminAPI.getUsers({
        search: searchTerm,
        plan: planFilter,
        status: statusFilter,
        page,
        limit: 50
      });
      
      setUsers(Array.isArray(response?.users) ? response.users : []);
      setCurrentPage(response?.page ?? 1);
      setTotalPages(response?.pages ?? 1);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
      setUsers([]);
    }
  };

  const handleUserClick = async (userId) => {
    try {
      const details = await adminAPI.getUserDetails(userId);
      setSelectedUserDetails(details);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const loadCostReport = async () => {
    try {
      setCostLoading(true);
      const data = await adminAPI.getCostReport();
      setCostReport(data);
    } catch (error) {
      console.error('Error loading cost report:', error);
      toast.error('Failed to load cost report');
    } finally {
      setCostLoading(false);
    }
  };

  const handleBlockUser = async (userId) => {
    try {
      await adminAPI.blockUser(userId);
      toast.success('User status updated');
      loadUsers(currentPage);
      if (selectedUserDetails && selectedUserDetails.user.id === userId) {
        const updated = await adminAPI.getUserDetails(userId);
        setSelectedUserDetails(updated);
      }
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  // Change Plan handlers
  const openChangePlanModal = (u) => {
    setChangePlanModal({
      open: true,
      user: u,
      selectedPlan: u.plan || 'free'
    });
  };

  const handleChangePlan = async () => {
    if (!changePlanModal.user) return;
    
    try {
      await adminAPI.updateUserPlan(changePlanModal.user.id, changePlanModal.selectedPlan);
      toast.success(`Plan updated to ${changePlanModal.selectedPlan}`);
      setChangePlanModal({ open: false, user: null, selectedPlan: '' });
      loadUsers(currentPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update plan');
    }
  };

  // Delete User handlers
  const openDeleteModal = (u) => {
    setDeleteModal({
      open: true,
      user: u,
      confirmText: ''
    });
  };

  const handleDeleteUser = async () => {
    if (!deleteModal.user || deleteModal.confirmText !== 'DELETE') return;
    
    try {
      await adminAPI.deleteUser(deleteModal.user.id);
      toast.success('User deleted permanently');
      setDeleteModal({ open: false, user: null, confirmText: '' });
      loadUsers(currentPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Reconciliation action handlers
  const handleSyncFromLemon = async (email) => {
    try {
      toast.loading('Syncing from Lemon Squeezy...', { id: 'sync-lemon' });
      const result = await adminAPI.syncUserFromLemon(email);
      
      if (result.success) {
        toast.success(`Synced ${email}: ${result.action_taken}`, { id: 'sync-lemon' });
        loadUsers(currentPage);
      } else {
        toast.error(result.message || 'Sync failed', { id: 'sync-lemon' });
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.detail || 'Failed to sync from Lemon Squeezy';
      toast.error(msg, { id: 'sync-lemon' });
    }
  };

  const handleMarkUnpaid = async (email) => {
    try {
      toast.loading('Marking as unpaid...', { id: 'mark-unpaid' });
      const result = await adminAPI.manualUpdateStatus(email, {
        subscription_status: 'unpaid',
        reason: 'Verified in Lemon Squeezy dashboard - subscription is unpaid'
      });
      
      if (result.success) {
        toast.success(`${email} marked as unpaid and downgraded to FREE`, { id: 'mark-unpaid' });
        loadUsers(currentPage);
      } else {
        toast.error('Failed to update status', { id: 'mark-unpaid' });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as unpaid', { id: 'mark-unpaid' });
    }
  };

  const handleForceDowngrade = async (email) => {
    try {
      toast.loading('Downgrading to free...', { id: 'force-downgrade' });
      const result = await adminAPI.manualUpdateStatus(email, {
        subscription_status: 'cancelled',
        reason: 'Admin forced downgrade to free plan'
      });
      
      if (result.success) {
        toast.success(`${email} downgraded to FREE plan`, { id: 'force-downgrade' });
        loadUsers(currentPage);
      } else {
        toast.error('Failed to downgrade', { id: 'force-downgrade' });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to downgrade', { id: 'force-downgrade' });
    }
  };

  const exportUsersCSV = () => {
    if (!users.length) return;
    
    const headers = ['Email', 'Plan', 'Account Status', 'Billing Status', 'Minutes Used', 'Minutes Remaining', 'Created', 'Last Login'];
    const rows = (users || []).map(u => {
      const plan = u?.plan || 'free';
      const accountStatus = u?.is_blocked ? 'blocked' : 'active';
      let billingStatus = 'n/a';
      if (plan !== 'free') {
        const subStatus = (u?.subscription_status || '').toLowerCase();
        billingStatus = subStatus || 'legacy';
      }
      return [
        u?.email || 'N/A',
        plan,
        accountStatus,
        billingStatus,
        u?.monthly_minutes_used || 0,
        u?.minutesRemaining || 0,
        u?.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A',
        u?.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : 'Never'
      ];
    });
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mb-4" />
          <p className="text-neutral-400">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="noise-overlay" />
      <Navbar />
      
      <div className="pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-unbounded font-bold mb-2">Admin Dashboard</h1>
              <p className="text-neutral-400">System overview and user management</p>
            </div>
          </div>

          {/* Overview Cards */}
          {overview && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="card-base p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-neutral-400 text-sm">Total Users</span>
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div className="text-3xl font-unbounded font-bold">{overview.totalUsers}</div>
                <div className="text-xs text-neutral-500 mt-1">
                  +{overview.newUsers24h || 0} today • +{overview.newUsers7d || 0} this week
                </div>
              </div>

              <div className="card-base p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-neutral-400 text-sm">Monthly Active</span>
                  <Activity className="w-5 h-5 text-green-500" />
                </div>
                <div className="text-3xl font-unbounded font-bold">{overview.mau || 0}</div>
                <div className="text-xs text-neutral-500 mt-1">
                  Active in last 30 days
                </div>
              </div>

              <div className="card-base p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-neutral-400 text-sm">Minutes Processed</span>
                  <Clock className="w-5 h-5 text-blue-500" />
                </div>
                <div className="text-3xl font-unbounded font-bold">{(overview.totalMinutes7d || 0).toFixed(0)}</div>
                <div className="text-xs text-neutral-500 mt-1">
                  Last 7 days • {(overview.totalMinutesAllTime || 0).toFixed(0)} all time
                </div>
              </div>

              <div className="card-base p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-neutral-400 text-sm">Success Rate</span>
                  <TrendingUp className="w-5 h-5 text-purple-500" />
                </div>
                <div className="text-3xl font-unbounded font-bold">{(overview.processingSuccessRate7d || 0).toFixed(0)}%</div>
                <div className="text-xs text-neutral-500 mt-1">
                  {overview.totalUploads7d || 0} uploads this week
                </div>
              </div>
            </div>
          )}

          {/* Trial Usage Distribution */}
          {overview && overview.trialUsageBuckets && Object.keys(overview.trialUsageBuckets).length > 0 && (
            <div className="card-base p-6 mb-8">
              <h2 className="text-xl font-bold mb-4">Trial Usage Distribution</h2>
              <div className="grid grid-cols-5 gap-4">
                {Object.entries(overview.trialUsageBuckets || {}).map(([bucket, count]) => (
                  <div key={bucket} className="text-center p-4 bg-neutral-800/50 rounded">
                    <div className="text-2xl font-bold text-primary">{count || 0}</div>
                    <div className="text-xs text-neutral-400">{bucket}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Monthly Growth Chart */}
          <div className="card-base p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Monthly Growth</h2>
              <select
                value={monthsToShow}
                onChange={(e) => {
                  setMonthsToShow(Number(e.target.value));
                  adminAPI.getMonthlyAnalytics(Number(e.target.value)).then(res => {
                    const arr = res?.data ?? [];
                    setMonthlyData(Array.isArray(arr) ? arr : []);
                  });
                }}
                className="input-field w-32"
              >
                <option value={6}>6 months</option>
                <option value={12}>12 months</option>
                <option value={24}>24 months</option>
              </select>
            </div>
            <div className="grid grid-cols-12 gap-2 h-32">
              {(monthlyData || []).slice(-12).map((m, idx) => {
                const maxUsers = Math.max(...(monthlyData || []).map(d => d?.users || 0), 1);
                const height = ((m?.users || 0) / maxUsers) * 100;
                return (
                  <div key={idx} className="flex flex-col items-center justify-end">
                    <div className="w-full bg-primary/20 rounded-t relative" style={{ height: `${height}%` }}>
                      <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-xs text-neutral-400">
                        {m?.users || 0}
                      </div>
                    </div>
                    <div className="text-xs text-neutral-500 mt-1">{m?.month?.slice(5) || ''}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Errors */}
          {overview && (overview.topErrors7d || []).length > 0 && (
            <div className="card-base p-6 mb-8">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                Top Errors (Last 7 Days)
              </h2>
              <div className="space-y-2">
                {(overview.topErrors7d || []).map((err, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-neutral-800/50 rounded">
                    <span className="text-sm text-neutral-300 flex-1 truncate">{err?.error || 'Unknown error'}</span>
                    <span className="text-sm font-bold text-red-400">{err?.count || 0}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Users Table */}
          <div className="card-base p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Users</h2>
              <Button onClick={exportUsersCSV} size="sm" className="btn-secondary">
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <input
                type="text"
                placeholder="Search by email or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field"
              />
              <select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                className="input-field"
              >
                <option value="">All Plans</option>
                <option value="free">Free</option>
                <option value="basic">Basic</option>
                <option value="pro">Pro</option>
                <option value="unlimited">Unlimited</option>
              </select>
              {/* Account Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input-field"
              >
                <option value="">All Billing Status</option>
                <option value="active">Active</option>
                <option value="on_trial">On Trial</option>
                <option value="past_due">Past Due</option>
                <option value="cancelled">Cancelled</option>
                <option value="expired">Expired</option>
                <option value="paused">Paused</option>
                <option value="legacy">Legacy (No Status)</option>
              </select>
              <Button onClick={() => loadUsers(1)} className="btn-primary">
                <Filter className="w-4 h-4 mr-2" />
                Apply Filters
              </Button>
            </div>

            {/* Users Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-800">
                    <th className="text-left p-3 text-sm font-semibold">Email</th>
                    <th className="text-left p-3 text-sm font-semibold" title="Stored plan from subscription">Stored Plan</th>
                    <th className="text-left p-3 text-sm font-semibold" title="Actual usable plan based on billing status">Effective</th>
                    <th className="text-left p-3 text-sm font-semibold">Account</th>
                    <th className="text-left p-3 text-sm font-semibold" title="subscription_status from Lemon Squeezy">LS Status</th>
                    <th className="text-left p-3 text-sm font-semibold">Minutes</th>
                    <th className="text-left p-3 text-sm font-semibold">Created</th>
                    <th className="text-left p-3 text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(users || []).map((u) => (
                    <tr 
                      key={u?.id || Math.random()} 
                      className="border-b border-neutral-800 hover:bg-neutral-800/30 cursor-pointer"
                      onClick={() => u?.id && handleUserClick(u.id)}
                    >
                      <td className="p-3 text-sm">{u?.email || 'N/A'}</td>
                      {/* Stored Plan */}
                      <td className="p-3 text-sm">
                        <span className={`px-2 py-1 rounded text-xs ${
                          u?.plan === 'pro' ? 'bg-purple-500/20 text-purple-400' :
                          u?.plan === 'basic' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-neutral-500/20 text-neutral-400'
                        }`}>
                          {u?.plan || 'free'}
                        </span>
                      </td>
                      {/* Effective Plan - calculated based on subscription status */}
                      <td className="p-3 text-sm">
                        {(() => {
                          const plan = u?.plan || 'free';
                          const status = u?.subscription_status?.toLowerCase() || '';
                          const billingStatus = u?.billing_status?.toLowerCase() || '';
                          
                          // Calculate effective plan (matches backend get_effective_plan logic)
                          const activeStatuses = ['active', 'on_trial'];
                          const isActive = activeStatuses.includes(status) && billingStatus !== 'inactive';
                          // unpaid/past_due: only keep plan if user has paid AND paid_access_until is in the future
                          const paidUntilStr = u?.paid_access_until;
                          const endDateStr = u?.subscription_end_date;
                          const hasPaid = u?.has_successful_payment === true;
                          const hasActiveBilling = ['unpaid', 'past_due'].includes(status) && hasPaid &&
                            paidUntilStr && new Date(paidUntilStr) > new Date();
                          // cancelled with future end date = grace period, keep plan
                          const cancelledWithAccess = status === 'cancelled' && endDateStr && new Date(endDateStr) > new Date();
                          const effectivePlan = plan === 'free' ? 'free' : 
                            (!status && billingStatus !== 'inactive') ? plan :  // Legacy users
                            isActive ? plan : 
                            cancelledWithAccess ? plan :
                            hasActiveBilling ? plan : 'free';
                          
                          const mismatch = effectivePlan !== plan;
                          
                          return (
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              mismatch ? 'bg-red-500/20 text-red-400' :
                              effectivePlan === 'pro' ? 'bg-purple-500/20 text-purple-400' :
                              effectivePlan === 'basic' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-neutral-500/20 text-neutral-400'
                            }`} title={mismatch ? `Stored: ${plan}, Effective: ${effectivePlan}` : ''}>
                              {effectivePlan}
                              {mismatch && ' ⚠️'}
                            </span>
                          );
                        })()}
                      </td>
                      {/* Account Status - active/blocked */}
                      <td className="p-3 text-sm">
                        {u?.is_blocked ? (
                          <span className="px-2 py-1 rounded bg-red-500/20 text-red-400 text-xs">blocked</span>
                        ) : (
                          <span className="px-2 py-1 rounded bg-green-500/20 text-green-400 text-xs">active</span>
                        )}
                      </td>
                      {/* Lemon Squeezy Status - subscription_status */}
                      <td className="p-3 text-sm">
                        {(() => {
                          const status = u?.subscription_status?.toLowerCase() || '';
                          const plan = u?.plan || 'free';
                          const billingStatus = u?.billing_status?.toLowerCase() || '';
                          
                          // Free plan - no billing status needed
                          if (plan === 'free') {
                            return <span className="text-neutral-500 text-xs">n/a</span>;
                          }
                          
                          // Billing explicitly inactive
                          if (billingStatus === 'inactive') {
                            return <span className="px-2 py-1 rounded bg-red-500/20 text-red-400 text-xs font-semibold">inactive</span>;
                          }
                          
                          // Past due - prominent red
                          if (status === 'past_due' || u?.payment_failed) {
                            return <span className="px-2 py-1 rounded bg-red-500/20 text-red-400 text-xs font-semibold">past_due</span>;
                          }
                          
                          // Active/on_trial - green
                          if (status === 'active' || status === 'on_trial') {
                            return <span className="px-2 py-1 rounded bg-green-500/20 text-green-400 text-xs">{status}</span>;
                          }
                          
                          // Cancelled/expired/paused - yellow (should trigger downgrade)
                          if (status === 'cancelled' || status === 'expired' || status === 'paused' || status === 'unpaid') {
                            return <span className="px-2 py-1 rounded bg-orange-500/20 text-orange-400 text-xs">{status}</span>;
                          }
                          
                          // No status set - legacy user
                          if (!status) {
                            return <span className="px-2 py-1 rounded bg-neutral-500/20 text-neutral-400 text-xs">legacy</span>;
                          }
                          
                          // Unknown status
                          return <span className="text-neutral-500 text-xs">{status}</span>;
                        })()}
                      </td>
                      <td className="p-3 text-sm">
                        {u?.monthly_minutes_used || 0} / {u?.minutesLimit || 0}
                      </td>
                      <td className="p-3 text-sm">
                        {u?.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="p-3 text-sm">
                        <div className="flex gap-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
                          {/* Re-sync from Lemon Squeezy - Available for ALL users */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSyncFromLemon(u?.email)}
                            className="text-xs px-2"
                            title="Re-sync from Lemon Squeezy (works for all users)"
                            data-testid={`resync-btn-${u?.email}`}
                          >
                            <RefreshCw className="w-3 h-3" />
                          </Button>
                          
                          {/* Billing Actions - Only for paid users */}
                          {u?.plan !== 'free' && (
                            <>
                              {/* Mark as Unpaid */}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleMarkUnpaid(u?.email)}
                                className="text-xs px-2 border-orange-500 text-orange-400 hover:bg-orange-500/20"
                                title="Mark subscription as unpaid (verified in Lemon dashboard)"
                              >
                                <AlertTriangle className="w-3 h-3" />
                              </Button>
                              
                              {/* Force Downgrade to Free */}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleForceDowngrade(u?.email)}
                                className="text-xs px-2 border-red-500 text-red-400 hover:bg-red-500/20"
                                title="Force downgrade to FREE plan"
                              >
                                <ChevronDown className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                          
                          {/* Change Plan - Only for super admin */}
                          {isSuperAdmin && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openChangePlanModal(u)}
                              className="text-xs"
                            >
                              <Edit className="w-3 h-3 mr-1" />
                              Plan
                            </Button>
                          )}
                          
                          {/* Block/Unblock */}
                          <Button
                            size="sm"
                            onClick={() => u?.id && handleBlockUser(u.id)}
                            className={u?.is_blocked ? 'btn-secondary text-xs' : 'bg-amber-600 hover:bg-amber-700 text-xs'}
                          >
                            {u?.is_blocked ? 'Unblock' : 'Block'}
                          </Button>
                          
                          {/* Delete - Only for super admin, not self */}
                          {isSuperAdmin && u?.email?.toLowerCase() !== SUPER_ADMIN_EMAIL.toLowerCase() && (
                            <Button
                              size="sm"
                              onClick={() => openDeleteModal(u)}
                              className="bg-red-600 hover:bg-red-700 text-xs"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-neutral-400">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => loadUsers(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="btn-secondary"
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  onClick={() => loadUsers(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="btn-secondary"
                >
                  Next
                </Button>
              </div>
            </div>
          </div>

          {/* User Details Modal */}
          {selectedUserDetails && (
            <div 
              className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6"
              onClick={() => setSelectedUserDetails(null)}
            >
              <div 
                className="card-base max-w-4xl w-full max-h-[80vh] overflow-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold">User Details</h2>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedUserDetails(null)}>
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <div className="text-sm text-neutral-400">Email</div>
                      <div className="font-semibold">{selectedUserDetails?.user?.email || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-neutral-400">Plan</div>
                      <div className="font-semibold">{selectedUserDetails?.user?.plan || 'free'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-neutral-400">Total Minutes (All Time)</div>
                      <div className="font-semibold">{selectedUserDetails?.user?.total_minutes_all_time || 0}</div>
                    </div>
                    <div>
                      <div className="text-sm text-neutral-400">Episodes Uploaded</div>
                      <div className="font-semibold">{selectedUserDetails?.user?.episodes_used || 0}</div>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold mb-3">Recent Episodes</h3>
                  <div className="space-y-2">
                    {(selectedUserDetails?.recentEpisodes || []).length === 0 ? (
                      <p className="text-neutral-400 text-sm">No episodes found</p>
                    ) : (
                      (selectedUserDetails?.recentEpisodes || []).slice(0, 10).map((ep) => (
                        <div key={ep?.id || Math.random()} className="p-3 bg-neutral-800/50 rounded">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-semibold">{ep?.title || 'Untitled'}</div>
                              <div className="text-xs text-neutral-400">
                                {(ep?.duration_minutes ?? 0).toFixed(1)} min • {ep?.status || 'unknown'}
                              </div>
                            </div>
                            <div className="text-xs text-neutral-500">
                              {ep?.created_at ? new Date(ep.created_at).toLocaleDateString() : 'N/A'}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="flex justify-end gap-2 mt-6">
                    <Button onClick={() => setSelectedUserDetails(null)} className="btn-secondary">
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Change Plan Modal */}
          {changePlanModal.open && (
            <div 
              className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6"
              onClick={() => setChangePlanModal({ open: false, user: null, selectedPlan: '' })}
            >
              <div 
                className="card-base max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <h2 className="text-xl font-bold mb-4">Change User Plan</h2>
                  <p className="text-neutral-400 text-sm mb-4">
                    Changing plan for: <span className="text-white font-semibold">{changePlanModal.user?.email}</span>
                  </p>
                  
                  <div className="mb-6">
                    <label className="block text-sm text-neutral-400 mb-2">Select Plan</label>
                    <select
                      value={changePlanModal.selectedPlan}
                      onChange={(e) => setChangePlanModal(prev => ({ ...prev, selectedPlan: e.target.value }))}
                      className="input-field w-full"
                    >
                      <option value="free">Free</option>
                      <option value="basic">Basic (€12/mo)</option>
                      <option value="pro">Pro (€29/mo)</option>
                      <option value="unlimited">Unlimited (€59/mo)</option>
                    </select>
                  </div>

                  <div className="flex justify-end gap-3">
                    <Button 
                      variant="outline" 
                      onClick={() => setChangePlanModal({ open: false, user: null, selectedPlan: '' })}
                    >
                      Cancel
                    </Button>
                    <Button 
                      className="btn-primary"
                      onClick={handleChangePlan}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete User Modal */}
          {deleteModal.open && (
            <div 
              className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6"
              onClick={() => setDeleteModal({ open: false, user: null, confirmText: '' })}
            >
              <div 
                className="card-base max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                      <Trash2 className="w-6 h-6 text-red-500" />
                    </div>
                    <h2 className="text-xl font-bold text-red-400">Delete User?</h2>
                  </div>
                  
                  <p className="text-neutral-300 mb-2">
                    This action permanently deletes the user and all related data.
                  </p>
                  <p className="text-red-400 font-semibold mb-4">
                    This cannot be undone.
                  </p>
                  
                  <p className="text-neutral-400 text-sm mb-2">
                    User: <span className="text-white font-semibold">{deleteModal.user?.email}</span>
                  </p>
                  
                  <div className="mb-6">
                    <label className="block text-sm text-neutral-400 mb-2">
                      Type <span className="text-red-400 font-bold">DELETE</span> to confirm
                    </label>
                    <Input
                      value={deleteModal.confirmText}
                      onChange={(e) => setDeleteModal(prev => ({ ...prev, confirmText: e.target.value }))}
                      placeholder="Type DELETE"
                      className="input-base w-full"
                    />
                  </div>

                  <div className="flex justify-end gap-3">
                    <Button 
                      variant="outline" 
                      onClick={() => setDeleteModal({ open: false, user: null, confirmText: '' })}
                    >
                      Cancel
                    </Button>
                    <Button 
                      className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleDeleteUser}
                      disabled={deleteModal.confirmText !== 'DELETE'}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* ============ COST ANALYTICS SECTION ============ */}
        <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 mt-8" data-testid="cost-analytics-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">Cost Analytics</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowCostAnalytics(!showCostAnalytics); if (!costReport) loadCostReport(); }}
              data-testid="toggle-cost-analytics"
              className="border-neutral-700 text-neutral-300 hover:bg-neutral-800"
            >
              {showCostAnalytics ? 'Hide' : 'Show'} Cost Analytics
            </Button>
          </div>

          {showCostAnalytics && (
            <div className="space-y-6">
              {costLoading ? (
                <p className="text-neutral-400 text-sm">Loading cost data...</p>
              ) : costReport ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-neutral-800/60 rounded-lg p-4">
                      <p className="text-xs text-neutral-500 uppercase tracking-wider">Avg Cost/Episode</p>
                      <p className="text-2xl font-bold text-emerald-400 mt-1">${costReport.averages?.cost_per_episode?.toFixed(4)}</p>
                    </div>
                    <div className="bg-neutral-800/60 rounded-lg p-4">
                      <p className="text-xs text-neutral-500 uppercase tracking-wider">Worst Case/Episode</p>
                      <p className="text-2xl font-bold text-amber-400 mt-1">${costReport.worst_case?.cost?.toFixed(4)}</p>
                    </div>
                    <div className="bg-neutral-800/60 rounded-lg p-4">
                      <p className="text-xs text-neutral-500 uppercase tracking-wider">Total Cost (All)</p>
                      <p className="text-2xl font-bold text-white mt-1">${costReport.totals?.total_cost?.toFixed(4)}</p>
                    </div>
                    <div className="bg-neutral-800/60 rounded-lg p-4">
                      <p className="text-xs text-neutral-500 uppercase tracking-wider">Episodes Analyzed</p>
                      <p className="text-2xl font-bold text-blue-400 mt-1">{costReport.total_episodes}</p>
                      <p className="text-xs text-neutral-500">{costReport.tracked_episodes} tracked / {costReport.estimated_episodes} estimated</p>
                    </div>
                  </div>

                  {/* Cost Breakdown */}
                  <div className="bg-neutral-800/60 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-neutral-300 mb-3">Cost Breakdown (Total)</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div><span className="text-neutral-500">Whisper STT:</span> <span className="text-white">${costReport.totals?.transcription?.toFixed(4)}</span></div>
                      <div><span className="text-neutral-500">GPT Models:</span> <span className="text-white">${costReport.totals?.gpt?.toFixed(4)}</span></div>
                      <div><span className="text-neutral-500">Retry Overhead:</span> <span className="text-white">${costReport.totals?.retry_overhead?.toFixed(4)}</span></div>
                      <div><span className="text-neutral-500">Infrastructure:</span> <span className="text-white">${costReport.totals?.infra?.toFixed(4)}</span></div>
                    </div>
                  </div>

                  {/* Profit Analysis - Average */}
                  <div className="bg-neutral-800/60 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-neutral-300 mb-3">Profit Analysis — Average Episode (${costReport.averages?.cost_per_episode?.toFixed(4)}/ep)</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="text-neutral-500 border-b border-neutral-700">
                          <th className="text-left p-2">Plan</th>
                          <th className="text-left p-2">Revenue</th>
                          <th className="text-left p-2">Episodes</th>
                          <th className="text-left p-2">Best Cost</th>
                          <th className="text-left p-2">Avg Cost</th>
                          <th className="text-left p-2">Worst Cost</th>
                          <th className="text-left p-2">Profit (Avg)</th>
                          <th className="text-left p-2">Margin</th>
                        </tr></thead>
                        <tbody>
                          {costReport.profit_analysis_avg && Object.entries(costReport.profit_analysis_avg).map(([plan, d]) => (
                            <tr key={plan} className="border-b border-neutral-800">
                              <td className="p-2 font-medium text-white capitalize">{plan}</td>
                              <td className="p-2 text-emerald-400">${d.revenue_usd?.toFixed(2)}</td>
                              <td className="p-2 text-neutral-400">{d.episodes_range}</td>
                              <td className="p-2 text-neutral-300">${d.cost_best_case?.toFixed(4)}</td>
                              <td className="p-2 text-neutral-300">${d.cost_avg_case?.toFixed(4)}</td>
                              <td className="p-2 text-amber-400">${d.cost_worst_case?.toFixed(4)}</td>
                              <td className="p-2 text-emerald-400">${d.profit_avg_case?.toFixed(2)}</td>
                              <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs font-medium ${d.margin_avg_pct > 90 ? 'bg-emerald-500/20 text-emerald-400' : d.margin_avg_pct > 70 ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'}`}>{d.margin_avg_pct}%</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Profit Analysis - Worst Case */}
                  <div className="bg-neutral-800/60 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-neutral-300 mb-3">Profit Analysis — Worst Case Episode (${costReport.worst_case?.cost?.toFixed(4)}/ep)</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="text-neutral-500 border-b border-neutral-700">
                          <th className="text-left p-2">Plan</th>
                          <th className="text-left p-2">Revenue</th>
                          <th className="text-left p-2">Episodes</th>
                          <th className="text-left p-2">Worst Cost</th>
                          <th className="text-left p-2">Profit (Worst)</th>
                          <th className="text-left p-2">Margin</th>
                          <th className="text-left p-2">Sustainable?</th>
                        </tr></thead>
                        <tbody>
                          {costReport.profit_analysis_worst && Object.entries(costReport.profit_analysis_worst).map(([plan, d]) => (
                            <tr key={plan} className="border-b border-neutral-800">
                              <td className="p-2 font-medium text-white capitalize">{plan}</td>
                              <td className="p-2 text-emerald-400">${d.revenue_usd?.toFixed(2)}</td>
                              <td className="p-2 text-neutral-400">{d.episodes_range}</td>
                              <td className="p-2 text-amber-400">${d.cost_worst_case?.toFixed(4)}</td>
                              <td className="p-2 text-emerald-400">${d.profit_worst_case?.toFixed(2)}</td>
                              <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs font-medium ${d.margin_worst_pct > 90 ? 'bg-emerald-500/20 text-emerald-400' : d.margin_worst_pct > 70 ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'}`}>{d.margin_worst_pct}%</span></td>
                              <td className="p-2 text-xs">{d.margin_worst_pct > 80 ? <span className="text-emerald-400">Safe</span> : d.margin_worst_pct > 50 ? <span className="text-amber-400">Watch</span> : <span className="text-red-400">Risk</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* User Profitability */}
                  {costReport.user_profitability?.length > 0 && (
                    <div className="bg-neutral-800/60 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-neutral-300 mb-3">User Profitability</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead><tr className="text-neutral-500 border-b border-neutral-700">
                            <th className="text-left p-2">User</th>
                            <th className="text-left p-2">Plan</th>
                            <th className="text-left p-2">Episodes</th>
                            <th className="text-left p-2">Cost</th>
                            <th className="text-left p-2">Revenue</th>
                            <th className="text-left p-2">Profit</th>
                            <th className="text-left p-2">Margin</th>
                          </tr></thead>
                          <tbody>
                            {costReport.user_profitability.map((u, i) => (
                              <tr key={i} className="border-b border-neutral-800">
                                <td className="p-2 text-neutral-300 text-xs">{u.email}</td>
                                <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs ${u.plan === 'pro' ? 'bg-purple-500/20 text-purple-400' : u.plan === 'basic' ? 'bg-blue-500/20 text-blue-400' : 'bg-neutral-500/20 text-neutral-400'}`}>{u.plan}</span></td>
                                <td className="p-2 text-neutral-300">{u.episodes}</td>
                                <td className="p-2 text-amber-400">${u.total_cost?.toFixed(4)}</td>
                                <td className="p-2 text-emerald-400">${u.monthly_revenue_usd?.toFixed(2)}</td>
                                <td className="p-2 text-emerald-400">${u.monthly_profit_usd?.toFixed(2)}</td>
                                <td className="p-2">{u.margin_pct != null ? <span className={`px-2 py-0.5 rounded text-xs ${u.margin_pct > 90 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>{u.margin_pct}%</span> : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Top Episodes by Cost */}
                  {costReport.episodes?.length > 0 && (
                    <div className="bg-neutral-800/60 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-neutral-300 mb-3">Episodes by Cost (Top {Math.min(costReport.episodes.length, 20)})</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead><tr className="text-neutral-500 border-b border-neutral-700">
                            <th className="text-left p-2">Title</th>
                            <th className="text-left p-2">Duration</th>
                            <th className="text-left p-2">Whisper</th>
                            <th className="text-left p-2">GPT</th>
                            <th className="text-left p-2">Total</th>
                            <th className="text-left p-2">Type</th>
                          </tr></thead>
                          <tbody>
                            {costReport.episodes.slice(0, 20).map((ep, i) => (
                              <tr key={i} className="border-b border-neutral-800">
                                <td className="p-2 text-neutral-300 text-xs max-w-[200px] truncate">{ep.title || ep.episode_id}</td>
                                <td className="p-2 text-neutral-400">{ep.duration_minutes?.toFixed(1) || '-'}m</td>
                                <td className="p-2 text-neutral-300">${ep.transcription_cost?.toFixed(4)}</td>
                                <td className="p-2 text-neutral-300">${ep.total_gpt_cost?.toFixed(4)}</td>
                                <td className="p-2 text-white font-medium">${ep.total_cost?.toFixed(4)}</td>
                                <td className="p-2 text-xs">{ep.is_estimated ? <span className="text-amber-400">est</span> : <span className="text-emerald-400">tracked</span>}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Pricing Reference */}
                  <div className="bg-neutral-800/60 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-neutral-300 mb-3">Pricing Reference (USD)</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs text-neutral-400">
                      <div>Whisper: ${costReport.pricing_reference?.whisper_per_minute}/min</div>
                      <div>GPT-4o input: ${costReport.pricing_reference?.gpt4o_input_per_1m}/1M tok</div>
                      <div>GPT-4o output: ${costReport.pricing_reference?.gpt4o_output_per_1m}/1M tok</div>
                      <div>GPT-4o-mini input: ${costReport.pricing_reference?.gpt4o_mini_input_per_1m}/1M tok</div>
                      <div>GPT-4o-mini output: ${costReport.pricing_reference?.gpt4o_mini_output_per_1m}/1M tok</div>
                      <div>Infra overhead: ${costReport.pricing_reference?.infra_per_episode}/episode</div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button variant="outline" size="sm" onClick={loadCostReport} className="border-neutral-700 text-neutral-300">
                      <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                    </Button>
                  </div>
                </>
              ) : (
                <p className="text-neutral-500 text-sm">Click "Show Cost Analytics" to load data</p>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Admin;
